<template>
  <teleport to="body">
    <div class="modal" v-if="showModal" @click.self.prevent="closeModal">
      <div class="wrapper">
        <font-awesome-icon icon="fa-xmark" class="exit" @click="closeModal" />
        <h2>Add Payment Method</h2>

        <section class="payment-container">
          <div class="content-box">
            <div class="subtitle">
              <h3>Payment Details</h3>
              <p>Securely add your payment method using Stripe.</p>
              <p v-if="companySettings.stripe_subscribed">Any new cards added will be set as your default payment method.</p>
            </div>
            <form @submit.prevent="createPaymentMethod" class="payment-form">
              <div class="form-group">
                <p>Email: {{ customerDetails.email }}</p>
              </div>

              <div class="form-group">
                <label for="card-element">Card Details</label>
                <div id="card-element" class="card-element">
                  <!-- Stripe Elements will create form elements here -->
                </div>
              </div>

              <div v-if="cardError" class="error-message">
                {{ cardError }}
              </div>

              <div class="form-actions">
                <button type="button" @click="closeModal" class="btn btn-cancel" :disabled="processing">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="!cardComplete || processing">
                  <span v-if="processing">Processing...</span>
                  <span v-else>Add Payment Method</span>
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapGetters } from "vuex";
import { getStripe } from "./stripe.js";
import { api } from "@monolith/helpers";

export default {
  name: "PaymentMethodModal",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    company: {
      type: String,
      required: true,
    },
  },
  emits: ["payment-method-created", "close"],
  data() {
    return {
      showModal: false,
      stripe: null,
      cardElement: null,
      cardComplete: false,
      cardError: null,
      processing: false,
    };
  },
  async mounted() {
    await this.initializeStripe();
  },
  methods: {
    async initializeStripe() {
      this.stripe = await getStripe();
      if (!this.stripe) {
        this.cardError = "Failed to load payment system. Please refresh and try again.";
      }
    },

    setupStripeElements() {
      // Clean up existing element
      if (this.cardElement) {
        try {
          this.cardElement.unmount();
        } catch (error) {
          // Element may already be unmounted
        }
        this.cardElement = null;
      }

      const cs = getComputedStyle(document.documentElement);
      const textColour = cs.getPropertyValue("--text-colour").trim();
      const actionColour = cs.getPropertyValue("--action-colour").trim();
      const secondaryColour = cs.getPropertyValue("--primary-colour").trim();

      const elements = this.stripe.elements();

      this.cardElement = elements.create("card", {
        style: {
          base: {
            fontSize: "16px",
            color: textColour,
            fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
            "::placeholder": {
              color: "#aab7c4",
            },
            iconColor: actionColour,
            backgroundColor: secondaryColour,
          },
          invalid: {
            color: "#fa755a",
            iconColor: "#fa755a",
          },
          complete: {
            color: textColour,
            iconColor: actionColour,
          },
        },
        hidePostalCode: true,
        disableLink: true,
      });

      this.$nextTick(() => {
        const cardElementContainer = document.getElementById("card-element");
        if (cardElementContainer && this.cardElement) {
          try {
            this.cardElement.mount("#card-element");
            this.handleAccessibility();
          } catch (error) {
            this.cardError = "Failed to load payment form. Please refresh and try again.";
          }
        }
      });

      // Event listeners for card state changes
      this.cardElement.on("change", ({ error, complete }) => {
        this.cardError = error ? error.message : null;
        this.cardComplete = complete;
      });

      this.cardElement.on("ready", () => {
        this.handleAccessibility();
      });

      this.cardElement.on("focus", () => {
        this.handleAccessibility();
      });
    },

    handleAccessibility() {
      const cardElementContainer = document.getElementById("card-element");
      if (cardElementContainer) {
        const iframes = cardElementContainer.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
          iframe.removeAttribute("aria-hidden");
          iframe.setAttribute("title", "Secure card payment input frame");
          iframe.setAttribute("aria-label", "Card details input");
        });
      }
    },

    async createPaymentMethod() {
      if (!this.stripe || !this.cardElement) return;

      this.processing = true;
      this.cardError = null;

      try {
        let response = await api.create_setup_intent(this.customerDetails.email, this.customerDetails.metadata.tenant_id, this.customerDetails.id);
        if (!response || !response.reply) {
          this.cardError = "Failed to initialize payment setup. Please try again.";
          return;
        }
        const result = await this.stripe.confirmCardSetup(response.reply, {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              email: this.customerDetails.email,
            },
          },
        });
        if (result.error) {
          this.cardError = result.error.message;
        } else if (result.setupIntent.status === "succeeded") {
          this.$emit("payment-method-created", result.setupIntent.payment_method);
          this.resetForm();
          this.closeModal();
        } else {
          this.cardError = "Payment setup was not completed. Please try again.";
        }
      } catch (error) {
        this.cardError = "An unexpected error occurred. Please try again.";
      } finally {
        this.processing = false;
      }
    },

    resetForm() {
      this.cardError = null;
      this.cardComplete = false;

      if (this.cardElement) {
        try {
          this.cardElement.clear();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();

      // Clean up Stripe element
      if (this.cardElement) {
        try {
          this.cardElement.blur();
          this.cardElement.unmount();
        } catch (error) {
          // Ignore cleanup errors
        }
        this.cardElement = null;
      }

      this.$emit("close");
    },
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.showModal = true;
        this.resetForm();

        // Setup Stripe elements after modal DOM is rendered
        if (this.stripe) {
          this.$nextTick(() => {
            this.setupStripeElements();
          });
        }
      } else {
        this.showModal = false;
      }
    },

    // Watch showModal to handle cleanup when modal closes
    showModal(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
  },
  computed: {
    ...mapGetters({
      companyName: "companyName",
      companySettings: "companySettings",
      customerDetails: "stripeCustomerDetails",
    }),
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";

.modal {
  .wrapper {
    width: 65%;
    max-width: 600px;
    background-color: var(--secondary-colour);
    border-radius: 20px;
    box-shadow: var(--box-shadow);
    padding: 2rem;
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    .exit {
      color: var(--action-colour);
      cursor: pointer;
    }

    h2 {
      color: var(--text-colour);
      text-align: center;
    }
  }

  .payment-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .content-box {
    background-color: var(--primary-colour);
    border-radius: 15px;
    box-shadow: var(--box-shadow);
    padding: 2rem;
    width: 100%;

    .subtitle {
      margin-bottom: 1.5rem;
      text-align: center;

      h3 {
        color: var(--text-colour);
        margin: 0 0 0.5rem 0;
      }

      p {
        color: var(--text-colour);
        margin: 0;
        opacity: 0.8;
      }
    }
  }

  .payment-form {
    .form-group {
      margin-bottom: 1.5rem;

      p {
        color: var(--text-colour);
      }

      label {
        display: block;
        color: var(--text-colour);
        margin-bottom: 0.5rem;
        font-weight: 500;
      }
      .card-element iframe {
        min-height: 40px;
        width: 100%;
        display: block !important;
        background-color: var(--secondary-colour);
      }

      .form-input {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid var(--action-colour);
        border-radius: 10px;
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        box-shadow: var(--box-shadow);
        font-size: 16px;

        &:focus {
          outline: none;
          border-color: var(--action-colour);
          box-shadow: 0 0 5px rgba(var(--action-colour-rgb), 0.3);
        }

        &::placeholder {
          color: #aab7c4;
        }
      }

    }
  }

  .error-message {
    background-color: #ff4444;
    color: white;
    padding: 0.75rem;
    border-radius: 10px;
    margin-bottom: 1rem;
    text-align: center;
    font-size: 14px;
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;

    .btn {
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 10px;
      box-shadow: var(--box-shadow);
      cursor: pointer;
      font-size: 16px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &:not(:disabled):hover {
        opacity: 0.9;
        transform: translateY(-1px);
      }

      &:not(:disabled):active {
        transform: scale(0.95);
      }

      &.btn-cancel {
        background-color: var(--primary-colour);
        color: var(--text-colour);
        border: 1px solid var(--action-colour);
      }

      &.btn-primary {
        background-color: var(--action-colour);
        color: var(--secondary-text-colour);
      }
    }
  }
}

@media only screen and (orientation: portrait) {
  .modal {
    .wrapper {
      width: 90%;
      margin: 1rem;
      padding: 1.5rem;
      max-height: 95vh;

      h2 {
        font-size: 1.5rem;
      }
    }

    .content-box {
      padding: 1.5rem;
    }

    .form-actions {
      flex-direction: column;
      gap: 0.5rem;

      .btn {
        width: 100%;
        padding: 1rem;
      }
    }
  }
}
</style>
